import { jwtDecode } from 'jwt-decode';

/**
 * Utilitário para diagnóstico da aplicação
 */
export const registrarErro = (categoria: string, mensagem: string, error?: any) => {
  const timestamp = new Date().toISOString();
  
  console.error(`[ERRO ${categoria}] ${timestamp} - ${mensagem}`);
  
  if (error) {
    console.error(`[DETALHE ERRO ${categoria}]`, error);
    
    // Se for uma resposta HTTP, tenta extrair mais detalhes
    if (error.status || error.statusText) {
      console.error(`[HTTP ERROR] Status: ${error.status} ${error.statusText}`);
      try {
        if (error.text && typeof error.text === 'function') {
          error.text().then((text: string) => {
            console.error('[RESPOSTA HTTP]', text.substring(0, 1000) + (text.length > 1000 ? '...' : ''));
          });
        }
      } catch (e) {
        // Ignorar erros ao tentar ler a resposta
      }
    }
  }
};

/**
 * Função para limpar cache de autenticação completamente
 */
export const limparCacheCompleto = (): void => {
  try {

    // Limpar sessionStorage
    const sessionKeys = Object.keys(sessionStorage);
    sessionKeys.forEach(key => {
      sessionStorage.removeItem(key);

    });
    
    // Limpar localStorage
    const localKeys = Object.keys(localStorage);
    localKeys.forEach(key => {
      localStorage.removeItem(key);

    });
    
    // Limpar cookies de autenticação
    const cookies = document.cookie.split(";");
    cookies.forEach(cookie => {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      
      if (name.includes('auth') || name.includes('token') || name.includes('session') || name.includes('user')) {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;

      }
    });

  } catch (error) {
    console.error('[CACHE] Erro durante limpeza de cache:', error);
  }
};

/**
 * Função para diagnosticar estado da autenticação
 */
export const diagnosticarAutenticacao = () => {
  const sessionToken = sessionStorage.getItem('token');
  const sessionUser = sessionStorage.getItem('user');
  const sessionEmpresa = sessionStorage.getItem('empresa');
  
  let tokenDecodificado = null;
  if (sessionToken) {
    try {
      tokenDecodificado = jwtDecode(sessionToken);
    } catch (error) {
      console.error('[DIAGNÓSTICO] Erro ao decodificar token:', error);
    }
  }
  
  const diagnostico = {
    sessionStorage: {
      token: sessionToken ? {
        presente: true,
        preview: sessionToken.substring(0, 20) + '...',
        tamanho: sessionToken.length,
        valido: !!tokenDecodificado
      } : { presente: false },
      user: sessionUser ? JSON.parse(sessionUser) : null,
      empresa: sessionEmpresa ? JSON.parse(sessionEmpresa) : null
    },
    tokenDecodificado,
    localStorage: {
      chaves: Object.keys(localStorage),
      valores: Object.keys(localStorage).reduce((acc, key) => {
        acc[key] = localStorage.getItem(key);
        return acc;
      }, {} as Record<string, string | null>)
    },
    cookies: document.cookie,
    url: window.location.href,
    timestamp: new Date().toISOString()
  };

  return diagnostico;
};

/**
 * NOVA: Função utilitária para fazer requisições autenticadas de diagnóstico
 */
export const diagnosticarImagensComAuth = async (): Promise<any> => {
  const token = sessionStorage.getItem('token');
  
  if (!token) {

    return { success: false, message: 'Token não disponível' };
  }
  
  try {
    // Usar API_BASE_URL da sessão ou detectar do ambiente
    const apiBaseUrl = sessionStorage.getItem('api_base_url') || 
                     process.env.REACT_APP_API_URL || 
                     'http://backend:3001'; // Usar backend por padrão em Docker

    // Garantir que a URL esteja corretamente formada sem duplicação de 'api'
    const endpoint = apiBaseUrl.includes('/api') 
      ? `${apiBaseUrl}/debug/list-profile-images` 
      : `${apiBaseUrl}/api/debug/list-profile-images`;
    
    const response = await fetch(endpoint, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (response.ok) {
      const data = await response.json();

      return data;
    } else {

    }
  } catch (error) {
    console.error('[DIAGNÓSTICO] Falha ao obter lista de imagens:', error);
    return { success: false, message: 'Erro de rede' };
  }
};

/**
 * Função para obter logs de erro
 */
export const obterLogsErro = (): any[] => {
  try {
    return JSON.parse(localStorage.getItem('erros_diagnostico') || '[]');
  } catch (error) {

    return [];
  }
};

/**
 * NOVO: Função para limpar logs de erro
 */
export const limparLogsErro = (): void => {
  if (typeof window !== 'undefined' && window.localStorage) {
    localStorage.removeItem('erros_diagnostico');
  }
};

export const verificarDados = <T>(dados: any, tipo: string): T[] => {
  if (!dados) {
    console.error(`[VERIFICAÇÃO] Dados de ${tipo} são nulos ou indefinidos`);
    return [];
  }
  
  if (!Array.isArray(dados)) {
    console.error(`[VERIFICAÇÃO] Dados de ${tipo} não são um array:`, typeof dados, dados);
    return [];
  }

  return dados as T[];
};

// Função auxiliar para validação de tipos de consulta
export const validarTiposConsulta = {
  statusConsulta: (valor: string): boolean => {
    const valoresValidos = ['agendada', 'realizada', 'cancelada', 'faltou'];
    return valoresValidos.includes(valor);
  },

  statusPagamento: (valor: string): boolean => {
    const valoresValidos = ['pendente', 'pago', 'cancelado'];
    return valoresValidos.includes(valor);
  },

  formaPagamento: (valor: string): boolean => {
    const valoresValidos = ['dinheiro', 'pix', 'cartao', 'transferencia'];
    return valoresValidos.includes(valor);
  }
};

// Validação de tipos de formulário
export const validarTiposFormulario = {
  escolaridade: (valor: string): boolean => {
    const valoresValidos = [
      '', 'fundamental_incompleto', 'fundamental_completo', 'medio_incompleto',
      'medio_completo', 'superior_incompleto', 'superior_completo',
      'pos_graduacao', 'mestrado', 'doutorado'
    ];
    return valoresValidos.includes(valor);
  },

  genero: (valor: string): boolean => {
    const valoresValidos = ['', 'masculino', 'feminino', 'outro', 'nao_informar'];
    return valoresValidos.includes(valor);
  },

  estadoCivil: (valor: string): boolean => {
    const valoresValidos = ['', 'solteiro', 'casado', 'divorciado', 'viuvo', 'uniao_estavel'];
    return valoresValidos.includes(valor);
  },

  servico: (valor: string): boolean => {
    const valoresValidos = ['psicoterapia', 'avaliacao', 'orientacao', 'terapia_casal', 'triagem'];
    return valoresValidos.includes(valor);
  }
};

/**
 * Utilitário para diagnóstico de performance
 */
export class PerformanceDiagnostic {
  private static startTimes = new Map<string, number>();

  static startTimer(label: string): void {
    this.startTimes.set(label, performance.now());
  }

  static endTimer(label: string): number {
    const startTime = this.startTimes.get(label);
    if (startTime === undefined) {

      return 0;
    }

    const duration = performance.now() - startTime;
    console.log(`[PERFORMANCE] ${label}: ${duration.toFixed(2)}ms`);
    this.startTimes.delete(label);
    return duration;
  }

  static logMemoryUsage(): void {
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      console.log('[MEMORY]', {
        used: `${(memory.usedJSHeapSize / 1048576).toFixed(2)} MB`,
        total: `${(memory.totalJSHeapSize / 1048576).toFixed(2)} MB`,
        limit: `${(memory.jsHeapSizeLimit / 1048576).toFixed(2)} MB`
      });
    }
  }
}
